/**
 * Permissions Hook
 * 
 * Provides centralized access to user permissions and privileges throughout the application.
 * Handles initialization, caching, and refreshing of permission data.
 * 
 * Features:
 * - Automatic initialization of permission services
 * - Periodic permission refresh to keep data current
 * - Convenient permission check methods for common operations
 * - Support for guided mode detection with local overrides
 * - First run transition support with cache invalidation
 * - Context-aware permission evaluation
 */
// @/hooks/permissions/usePermissions.js
import { useState, useEffect, useCallback } from 'react';
import { permissionsService } from '@/services/permissions/permissions';
import { privilegesService } from '@/services/permissions/privilegesService';

/**
 * React hook for accessing permissions and privileges data
 * Enhanced with better first run transition support
 *
 * @returns {Object} Permission methods and state for use in components
 */
export function usePermissions() {
  // Track permissions initialization state
  const [initialized, setInitialized] = useState(permissionsService.initialized);
  
  // Track privileges data for UI needs (guided mode, etc)
  const [projectPrivileges, setProjectPrivileges] = useState(
    privilegesService.projectPrivileges || privilegesService.getDefaultPrivileges()
  );

  /**
   * Initialize permissions if not already initialized.
   *
   * Race condition (TÜV SÜD enterprise-CA-invisible bug, #882):
   * On a fast-path reload, `permissionsService.initialized` can already be `true`
   * by the time this hook mounts, while `privilegesService.projectPrivileges` is
   * still `null` (its fetch is in flight or just timed out) — OR worse — it's
   * a stale GRANTED_FREE restored from sessionStorage (Edge tab-restore, prior
   * partial-failure session). The old guard `if (!initialized)` skipped
   * re-fetching in that window; the previous fix only caught the null case
   * but not the stale-cache case.
   *
   * Fix: re-init when privileges haven't been verified against the source in
   * this session, and force a fresh fetch in that case. The cache is allowed
   * for first paint (so we don't block render), but we always validate it.
   */
  useEffect(() => {
    const privilegesMissing = !privilegesService.projectPrivileges;
    const cacheUnverified = !privilegesService.inMemoryFetchedThisSession;

    if (!initialized || privilegesMissing || cacheUnverified) {
      const initPermissions = async () => {
        try {
          if (!permissionsService.initialized) {
            await permissionsService.initialize();
          }

          // Always force a fresh fetch when the cache hasn't been verified
          // this session. Subscriber callback will re-render if the value
          // arrives after we've already set initial state from cache.
          const privileges = await privilegesService.getPrivileges(true);

          setInitialized(true);
          setProjectPrivileges(
            privileges || privilegesService.getDefaultPrivileges()
          );
        } catch (error) {
          // Set initialized anyway to prevent retries
          setInitialized(true);
        }
      };

      initPermissions();
    }
  }, [initialized]);

  /**
   * Subscribe to privilege updates so background refreshes (route navigation,
   * periodic check, _loadIdentityData completion) flow into React state. Without
   * this, the service updates `projectPrivileges` in memory but the UI keeps
   * rendering the value seeded at hook mount — that's why "click around" used
   * to fix the bug while reload didn't.
   */
  useEffect(() => {
    const unsubscribe = privilegesService.subscribe((newPrivileges) => {
      setProjectPrivileges(
        newPrivileges || privilegesService.getDefaultPrivileges()
      );
    });
    return unsubscribe;
  }, []);

  /**
   * Periodically check if permissions or privileges need refresh
   */
  useEffect(() => {
    if (initialized) {
      const checkRefresh = async () => {
        try {
          // Check if permissions need refresh
          if (permissionsService.needsRefresh()) {
            await permissionsService.refreshPermissions();
          }

          // Check if privileges need refresh
          if (privilegesService.needsRefresh()) {
            const updatedPrivileges = await privilegesService.getPrivileges();
            setProjectPrivileges(updatedPrivileges);
          }
        } catch (error) {
          // Never let refresh errors crash the app — use cached/default privileges
          if (process.env.NODE_ENV === 'development') {
            console.warn('[usePermissions] Refresh failed, using cached data:', error.message);
          }
        }
      };

      // Check once on mount
      checkRefresh();

      // Set up interval for periodic checks (every 5 minutes)
      const intervalId = setInterval(checkRefresh, 5 * 60 * 1000);
      return () => clearInterval(intervalId);
    }
  }, [initialized]);

  /**
   * Check if user has a specific permission
   * 
   * @param {string} permission - Permission key to check
   * @param {Object} context - Optional context data for permission evaluation
   * @returns {boolean} Whether user has the permission
   */
  const hasPermission = useCallback((permission, context = {}) => {
    return permissionsService.hasPermission(permission, context);
  }, []);

  /**
   * Check if user is an admin
   * 
   * @returns {boolean} Whether user has admin role
   */
  const isAdmin = useCallback(() => {
    return permissionsService.isAdmin();
  }, []);

  /**
   * Check if user is the main user for the company
   * 
   * @returns {boolean} Whether user is the main user
   */
  const isMainUser = useCallback(() => {
    return permissionsService.isMainUser();
  }, []);

  /**
   * Check if user can edit profile information
   * 
   * @returns {boolean} Whether user can edit profile
   */
  const canEditProfile = useCallback(() => {
    return permissionsService.canEditProfile();
  }, []);

  /**
   * Enhanced refreshPermissions function that updates both
   * permissions and privileges services with better error handling
   * FIXED: Added forceInvalidateCache option for first run transitions
   * 
   * @param {boolean} forceInvalidateCache - Force invalidation of cached data
   * @returns {Promise<boolean>} Success status of refresh operation
   */
  const refreshPermissions = useCallback(async (forceInvalidateCache = false) => {
    try {
      // Log in development mode
      if (process.env.NODE_ENV === 'development') {
        console.log(`Refreshing permissions (forceInvalidateCache: ${forceInvalidateCache})`);
      }
      
      // If force invalidate is requested, clear cached privileges first
      if (forceInvalidateCache && privilegesService.clearPrivileges) {
        privilegesService.clearPrivileges();
      }
      
      // First refresh privileges (which permissions depend on)
      const updatedPrivileges = await privilegesService.getPrivileges(forceInvalidateCache);
      setProjectPrivileges(updatedPrivileges);
      
      // Then refresh permissions based on updated privileges
      const result = await permissionsService.refreshPermissions(forceInvalidateCache);
      
      // Log success in development mode
      if (process.env.NODE_ENV === 'development') {
        console.log('Permissions refreshed successfully:', 
          { 
            appMode: updatedPrivileges?.AppMode,
            carbonAccess: updatedPrivileges?.CarbonAccounting 
          }
        );
      }
      
      return result;
    } catch (error) {
      // Log error in development mode
      if (process.env.NODE_ENV === 'development') {
        console.error('Error refreshing permissions:', error);
      }
      
      throw error;
    }
  }, []);

  /**
   * Check if the current project is a lead firm with CA item+s access
   * Lead firms see CA item+s dashboards instead of regular Carbon Accounting
   *
   * @returns {boolean} Whether the project has GRANTED_ITEMS privilege
   */
  const isLeadFirm = useCallback(() => {
    return projectPrivileges?.CarbonAccounting === 'GRANTED_ITEMS';
  }, [projectPrivileges]);

  /**
   * Get the Carbon Accounting privilege type
   *
   * @returns {string} 'GRANTED_FREE' | 'GRANTED_ITEMS'
   */
  const getCarbonAccountingType = useCallback(() => {
    return projectPrivileges?.CarbonAccounting || 'GRANTED_FREE';
  }, [projectPrivileges]);

  /**
   * Check if in guided mode (based on privileges)
   * FIXED: Added local overrides for first run completion
   *
   * @returns {boolean} Whether the application is in guided mode
   */
  const isGuidedMode = useCallback(() => {
    // First check for local override from localStorage
    if (typeof window !== 'undefined') {
      const firstRunComplete = localStorage.getItem('firstRunComplete') === 'true';
      
      // If first run completed, override server values temporarily
      if (firstRunComplete) {
        return false;
      }
    }
    
    // Otherwise use server value
    return projectPrivileges?.AppMode === "GUIDED";
  }, [projectPrivileges]);

  // Return all permission methods and state
  return {
    // Core methods - direct delegates to permissionsService
    hasPermission,
    isAdmin,
    isMainUser,
    canEditProfile,
    isGuidedMode,
    isLeadFirm,
    getCarbonAccountingType,

    // User management permissions - wrappers around hasPermission
    canCreateUsers: () => hasPermission('user:create'),
    canManageUsers: () => hasPermission('user:list'),
    canDeleteUser: (targetUser) => hasPermission('user:delete', { targetUser }),

    // Module access permissions
    canAccessCarbonAccounting: () => hasPermission('module:carbon-accounting'),

    // Status tracking
    initialized,
    permissionsInitialized: initialized,

    // Project privileges data for UI needs
    projectPrivileges,

    // Enhanced refresh method
    refreshPermissions
  };
}